import React from 'react';
import { XMarkIcon, DocumentTextIcon, LinkIcon } from '@heroicons/react/24/outline';
import { DoctorClinicAppointment } from '../../models/MasterAdmin/doctorClinicAppointment';
import { fetchClinicAppointmentInvoiceBlob } from '../../services/MasterAdmin/doctorClinicService';

const CLINIC_INVOICE_ELIGIBLE_PAYMENT_STATUSES = new Set(['paid']);
const CLINIC_INVOICE_ELIGIBLE_STATUSES = new Set(['confirmed', 'completed', 'postponed']);

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatInr = (amount: number | null | undefined) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const capitalize = (value: string | null | undefined) => {
  if (!value) return '—';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const statusBadgeClass = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
    case 'pending':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
    case 'cancelled':
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
    case 'confirmed':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
    case 'postponed':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
  }
};

const paymentBadgeClass = (status: string) => {
  switch (status.toLowerCase()) {
    case 'paid':
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
    case 'pending':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
    case 'failed':
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
  }
};

type DetailRowProps = {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
};

const DetailRow: React.FC<DetailRowProps> = ({ label, value, mono }) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt>
    <dd
      className={`mt-1 text-sm text-gray-900 dark:text-white break-words ${mono ? 'font-mono text-xs' : ''}`}
    >
      {value ?? '—'}
    </dd>
  </div>
);

type Props = {
  appointment: DoctorClinicAppointment | null;
  isOpen: boolean;
  onClose: () => void;
};

const DoctorClinicAppointmentDetailsModal: React.FC<Props> = ({ appointment, isOpen, onClose }) => {
  if (!isOpen || !appointment) return null;

  const normalizedPaymentStatus = String(appointment.paymentStatus || '').toLowerCase();
  const normalizedStatus = String(appointment.status || '').toLowerCase();
  const canViewInvoice =
    CLINIC_INVOICE_ELIGIBLE_PAYMENT_STATUSES.has(normalizedPaymentStatus) &&
    CLINIC_INVOICE_ELIGIBLE_STATUSES.has(normalizedStatus);

  const shortId = appointment.clinicAppointmentId
    ? `${appointment.clinicAppointmentId.slice(0, 8)}…`
    : '—';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="appointment-details-title"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Close modal"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl rounded-t-2xl bg-white shadow-xl dark:bg-gray-800 sm:max-h-[min(92dvh,48rem)] sm:rounded-2xl sm:overflow-hidden flex flex-col">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5">
          <div className="min-w-0">
            <h2 id="appointment-details-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Appointment Details
            </h2>
            <p className="mt-0.5 truncate font-mono text-xs text-gray-500 dark:text-gray-400" title={appointment.clinicAppointmentId}>
              {shortId}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-6">
          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
              Patient
            </h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailRow label="Name" value={appointment.user?.name} />
              <DetailRow label="Phone" value={appointment.user?.phone_number} />
              <DetailRow label="Email" value={appointment.user?.emailId} />
              <DetailRow label="User ID" value={appointment.userId} mono />
            </dl>
          </section>

          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
              Appointment
            </h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailRow
                label="Type"
                value={appointment.isOnline ? 'Online consultation' : 'Offline consultation'}
              />
              <DetailRow label="Date" value={appointment.date} />
              <DetailRow label="Time" value={appointment.time} />
              <DetailRow
                label="Status"
                value={
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusBadgeClass(appointment.status)}`}>
                    {capitalize(appointment.status)}
                  </span>
                }
              />
              <DetailRow
                label="User response"
                value={
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusBadgeClass(appointment.userResponseStatus)}`}>
                    {capitalize(appointment.userResponseStatus)}
                  </span>
                }
              />
              {appointment.isOnline && appointment.meetingUrl ? (
                <DetailRow
                  label="Meeting link"
                  value={
                    <a
                      href={appointment.meetingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
                    >
                      <LinkIcon className="h-4 w-4 shrink-0" />
                      Open meeting
                    </a>
                  }
                />
              ) : null}
            </dl>
          </section>

          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
              Payment
            </h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailRow
                label="Payment status"
                value={
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${paymentBadgeClass(appointment.paymentStatus)}`}>
                    {capitalize(appointment.paymentStatus)}
                  </span>
                }
              />
              {Number(appointment.originalAmountBeforeDiscount || 0) > 0 ? (
                <DetailRow
                  label="Consultation fee"
                  value={formatInr(appointment.originalAmountBeforeDiscount)}
                />
              ) : Number(appointment.acceptedInstantFee || 0) > 0 ? (
                <DetailRow
                  label="Consultation fee"
                  value={formatInr(appointment.acceptedInstantFee)}
                />
              ) : null}
              {Number(appointment.discountAmount || 0) > 0 ? (
                <DetailRow
                  label="Discount"
                  value={formatInr(appointment.discountAmount)}
                />
              ) : null}
              <DetailRow label="Paid amount" value={formatInr(appointment.paidAmount)} />
            </dl>
          </section>

          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
              System
            </h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailRow label="Appointment ID" value={appointment.clinicAppointmentId} mono />
              <DetailRow label="Doctor ID" value={appointment.doctorId} mono />
              <DetailRow label="Vendor ID" value={appointment.vendorId} mono />
              <DetailRow label="Created at" value={formatDateTime(appointment.createdAt)} />
              <DetailRow label="Updated at" value={formatDateTime(appointment.updatedAt)} />
              <DetailRow label="Admin updated at" value={formatDateTime(appointment.adminUpdatedAt)} />
            </dl>
          </section>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-gray-200 p-4 dark:border-gray-700 sm:flex-row sm:justify-end sm:p-5">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:w-auto"
          >
            Close
          </button>
          {canViewInvoice ? (
            <button
              type="button"
              onClick={async () => {
                try {
                  const invoiceBlob = await fetchClinicAppointmentInvoiceBlob(
                    appointment.clinicAppointmentId,
                    'masteradmin'
                  );
                  const blobUrl = URL.createObjectURL(invoiceBlob);
                  window.open(blobUrl, '_blank', 'noopener,noreferrer');
                  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                } catch (err) {
                  console.error('Failed to open master admin clinic invoice PDF:', err);
                  window.alert('Failed to open master admin invoice. Please try again.');
                }
              }}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2.5 text-sm font-medium text-violet-700 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-900/20 dark:text-violet-300 dark:hover:bg-violet-900/30 sm:w-auto"
            >
              <DocumentTextIcon className="h-4 w-4" />
              Master Admin Invoice
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DoctorClinicAppointmentDetailsModal;
